import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Col, Container, Row } from "reactstrap";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import EngineHeader from "./EngineHeader";
import CylinderSection from "./CylinderSection";

/* ─────────────────────────────────────────
   Helpers
───────────────────────────────────────── */
const getSection = (sections, id) =>
  sections.find((s) => s.id === id) || { metrics: [] };

const getMetric = (section, label) =>
  section.metrics?.find((m) => m.label === label) || {
    value: "--",
    unit: "",
    status: "normal",
  };

const statusColor = (status) => {
  switch (status) {
    case "alarm":   return "#ef4444";
    case "warning": return "#f59e0b";
    default:        return "#22c55e";
  }
};

const statusLabel = (status) => {
  switch (status) {
    case "alarm":   return "Alarm";
    case "warning": return "Warning";
    default:        return "Normal";
  }
};

/* ─────────────────────────────────────────
   Gauge Component
───────────────────────────────────────── */
const Gauge = ({ value, unit, label, color, subtitle, subtitleValue }) => {
  const numVal = parseFloat(value) || 0;
  const angle = Math.min(Math.max((numVal / 100) * 180, 0), 180);

  const polarToCartesian = (cx, cy, r, deg) => {
    const rad = ((deg - 180) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const describeArc = (cx, cy, r, startDeg, endDeg) => {
    const s = polarToCartesian(cx, cy, r, startDeg);
    const e = polarToCartesian(cx, cy, r, endDeg);
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
  };

  return (
    <div className="gauge-card">
      <div className="gauge-label-top">{label}</div>
      <div className="gauge-svg-wrap">
        <svg viewBox="0 0 120 70" width="160" height="65">
          {/* Track */}
          <path
            d={describeArc(60, 60, 48, 0, 180)}
            fill="none"
            stroke="#e8edf3"
            strokeWidth="7"
            strokeLinecap="round"
          />
          {/* Fill */}
          <path
            d={describeArc(60, 60, 48, 0, angle)}
            fill="none"
            stroke={color}
            strokeWidth="7"
            strokeLinecap="round"
          />
          {/* Value */}
          <text x="60" y="54" textAnchor="middle" fontSize="14" fontWeight="700" fill="#1a2332">
            {value}
          </text>
          <text x="60" y="66" textAnchor="middle" fontSize="11"fontWeight="700" fill="#000000">
            {unit}
          </text>
        </svg>
      </div>
      <div className="gauge-footer">
        <span className="gauge-dot" style={{ background: "#22c55e" }} />
        <span className="gauge-sub-label">{subtitle}</span>
        <span className="gauge-sub-val">{subtitleValue}</span>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   System Row
───────────────────────────────────────── */
const SysRow = ({ label, metric }) => (
  <div className="sys-row">
    <span className="sys-dot" style={{ background: statusColor(metric.status) }} />
    <span className="sys-label">{label}</span>
    <span className="sys-value">{metric.value}</span>
    <span className="sys-unit">{metric.unit}</span>
  </div>
);

/* ─────────────────────────────────────────
   Perf Card
───────────────────────────────────────── */
const PerfCard = ({ icon, label, metric, subLabel }) => (
  <div className="perf-card">
    <div className="perf-icon">{icon}</div>
    <div className="perf-info">
      <div className="perf-lbl">{label}</div>
      <div className="perf-val">
        {metric.value} <span className="perf-unit">{metric.unit}</span>
      </div>
      <div className="perf-status" style={{ color: statusColor(metric.status) }}>
        {subLabel || statusLabel(metric.status)}
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────
   Main Component
───────────────────────────────────────── */
const Equip2 = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [fuelChartData, setFuelChartData] = useState([
    { time: "00:00", value: 1650 },
    { time: "01:00", value: 1720 },
    { time: "02:00", value: 1800 },
    { time: "03:00", value: 1780 },
    { time: "04:00", value: 1820 },
    { time: "05:00", value: 1760 },
    { time: "06:00", value: 1900 },
    { time: "07:00", value: 1850 },
    { time: "08:00", value: 1820 },
  ]);

  const toMayDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    return `2026-05-${day}`;
  };

  const getCurrentTime = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:00`;
  };

  const fetchDashboard = async (selDate) => {
    try {
      const apiDate = toMayDate(selDate);
      const currentTime = getCurrentTime();
      const response = await axios.get(
        `https://api.hanoch.in/api/engine-dashboard/?date=${apiDate}&time=${currentTime}`
      );
      const data = response.data || response;
      setSections(data.sections || []);

      const fuelSection = getSection(data.sections || [], "fuel_oil");
      const fuelMetric = getMetric(fuelSection, "FO Consumption / Hour");
      const now = new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

      setFuelChartData((prev) => {
        const updated = [...prev, { time: now, value: Number(fuelMetric.value || 0) }];
        return updated.slice(-10);
      });
    } catch (error) {
      console.error("Dashboard Fetch Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard(selectedDate);
    const interval = setInterval(() => fetchDashboard(selectedDate), 60000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  /* Sections */
  const turbo = getSection(sections, "turbo_charger");
  const shaft  = getSection(sections, "shaft_power_system");
  const lube   = getSection(sections, "lube_oil");
  const fuel   = getSection(sections, "fuel_oil");
  const scav   = getSection(sections, "scavenge_air");
  const boiler = getSection(sections, "control_&_boiler");

  /* Fuel chart stats */
  const vals = fuelChartData.map((d) => d.value).filter(Boolean);
  const fcAvg = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
  const fcMin = vals.length ? Math.min(...vals) : 0;
  const fcMax = vals.length ? Math.max(...vals) : 0;
  const fcTotal = vals.length ? vals.reduce((a, b) => a + b, 0) : 0;

  /* Active alerts mock (replace with API when available) */
  const alerts = [
    {
      type: "warning",
      title: "High Exhaust Gas Temperature - Cylinder 4",
      desc: "412 °C exceeds warning limit",
      time: "03:32:15",
    },
    {
      type: "info",
      title: "Boiler Fuel Temperature High",
      desc: "92 °C is above normal range",
      time: "03:28:47",
    },
    {
      type: "sched",
      title: "Scheduled Maintenance Due Soon",
      desc: "Main Engine - in 98 operating hours",
      time: "03:20:11",
    },
  ];

  const alertIcon = (type) => {
    if (type === "warning") return { icon: "⚠", color: "#ef4444", bg: "#fef2f2" };
    if (type === "info") return { icon: "⚠", color: "#f59e0b", bg: "#fffbeb" };
    return { icon: "ℹ", color: "#3b82f6", bg: "#eff6ff" };
  };

  return (
    <React.Fragment>
      <div className="page-content">
      <style>{`
        .eq2-page { background: #f0f4f8; min-height: 100vh; font-family: 'Segoe UI', system-ui, sans-serif; }
        .eq2-inner { padding: 0 16px 16px; max-width: 100%; }

        /* ── TOP GAUGE ROW ── */
        .gauge-row { display: grid; grid-template-columns: repeat(6,1fr); gap: 12px; margin-bottom: 5px; }
        .gauge-card { background:#fff; border-radius:10px; padding:14px 10px 10px; display:flex; flex-direction:column; align-items:center; box-shadow:0 1px 4px rgba(0,0,0,.06); }
        .gauge-label-top { font-size:11px; font-weight:700; letter-spacing:.6px; color:#1a73e8; text-transform:uppercase; margin-bottom:4px; text-align:center; }
        .gauge-svg-wrap { display:flex; justify-content:center; }
        .gauge-footer { display:flex; align-items:center; gap:5px; margin-top:4px; }
        .gauge-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
        .gauge-sub-label { font-size:10px; font-weight:700; color:#070707; }
        .gauge-sub-val { font-size:10px; color:#070707; font-weight:700; }

        /* ── MAIN GRID ── */
        .main-grid { display:grid; grid-template-columns: 285px 1fr 340px; gap:14px; align-items:start; }

        /* ── ENGINE SYSTEMS PANEL ── */
        .sys-panel { background:#fff;height:404px; border-radius:10px; padding:16px; box-shadow:0 1px 4px rgba(0,0,0,.06); }
        .panel-title { font-size:12px;color: #1a73e8; font-weight:700; text-transform:uppercase; letter-spacing:.5px; margin-bottom:12px; display:flex; align-items:center; gap:6px; }
        .panel-title-icon { color:#1a73e8; }
        .sys-row { display:flex; align-items:center; gap:8px; padding:9px 0; border-bottom:1px solid #f0f4f8; }
        .sys-row:last-child { border-bottom:none; }
        .sys-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
        .sys-label { flex:1; font-size:14px; color:#4a5568; }
        .sys-value { font-size:13px; font-weight:700; color:#1a2332; }
        .sys-unit { font-size:10px; color:#6b7a90; margin-left:2px; min-width:24px; }
        .view-all { font-size:11px; color:#1a73e8; font-weight:600; cursor:pointer; margin-top:10px; display:inline-flex; align-items:center; gap:4px; text-decoration:none; }

        /* ── CENTER COLUMN ── */
        .center-col { display:flex; flex-direction:column; gap:12px; }

        /* Engine card */
/* Engine card */
.engine-card { background:#fff; border-radius:10px; box-shadow:0 1px 4px rgba(0,0,0,.06); overflow:hidden; }
.engine-card-header { display:flex; align-items:center; justify-content:space-between; padding:14px 16px 10px; }
.engine-card-title { font-size:13px; font-weight:700; color:#1a73e8; text-transform:uppercase; letter-spacing:.5px; display:flex; align-items:center; gap:8px; }
.engine-model-badge { background:#e8f0fe; color:#1a73e8; font-size:11px; font-weight:600; padding:3px 10px; border-radius:6px; }
.view-details-btn { font-size:12px; color:#1a73e8; font-weight:600; cursor:pointer; display:flex; align-items:center; gap:4px; }
.engine-img-wrap { padding:0 14px; }
.engine-img-wrap video, .engine-img-wrap img { width:100%; height:260px; object-fit:cover; border-radius:10px; border:1px solid #e8edf3; }
.engine-stats-bar { display:grid; grid-template-columns:repeat(6,1fr); padding:12px 16px 14px; border-top:1px solid #f0f4f8; gap:4px; margin-top:10px; }
.engine-stat { display:flex; flex-direction:column; align-items:center; padding:6px 4px; background:#f8fafc; border-radius:6px; }
.engine-stat-label { font-size:9px; color:#6b7a90; text-transform:uppercase; letter-spacing:.4px; text-align:center; line-height:1.4; }
.engine-stat-value { font-size:11px; font-weight:700; color:#1a2332; margin-top:3px; text-align:center; }
.engine-stat-value.running { color:#22c55e; }

        /* Cylinder section wrapper */
        .cylinder-wrap { background:#fff; border-radius:10px; box-shadow:0 1px 4px rgba(0,0,0,.06); overflow:hidden; }

        /* Performance status */
       /* Performance status */
.perf-panel { 
  background:#fff; 
  border-radius:10px; 
  padding:16px 20px; 
  box-shadow:0 1px 4px rgba(0,0,0,.06); 
  height:210px;
}
.perf-grid { 
  display:grid; 
  grid-template-columns:repeat(6,1fr); 
  gap:10px; 
  margin-top:12px; 
}
.perf-card { 
  display:flex; 
  flex-direction:column; 
  align-items:flex-start; 
  gap:6px; 
  padding:15px; 
  background:#f8fafc; 
  border-radius:8px; 
  border: 1px solid #e8edf3;
}
.perf-icon { 
  font-size:22px; 
}
.perf-info { 
  display:flex; 
  flex-direction:column; 
}
.perf-lbl { 
  font-size:10px; 
  color:#6b7a90; 
  text-transform:uppercase; 
  letter-spacing:.5px; 
  font-weight:600; 
  line-height:1.3;
}
.perf-val { 
  font-size:16px; 
  font-weight:700; 
  color:#1a2332; 
  margin-top:4px; 
  line-height:1.2;
}
.perf-unit { 
  font-size:11px; 
  font-weight:400; 
  color:#6b7a90; 
}
.perf-status { 
  font-size:11px; 
  font-weight:600; 
  margin-top:3px; 
}
        /* ── RIGHT COLUMN ── */
        .right-col { display:flex; flex-direction:column; gap:12px; }

        /* Fuel chart */
        .fuel-panel { background:#fff;height:257px; border-radius:10px; padding:14px 16px; box-shadow:0 1px 4px rgba(0,0,0,.06); }
        .fuel-panel-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
        .fuel-panel-title { font-size:12px; font-weight:700; color: #1a73e8; }
        .fuel-time-sel { font-size:11px; color:#1a73e8; border:1px solid #d0d9e6; border-radius:4px; padding:2px 6px; background:#fff; cursor:pointer; }
        .fuel-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; margin-top:10px; border-top:1px solid #f0f4f8; padding-top:10px; }
        .fuel-stat { display:flex;background: #f8fafc; padding:5px; flex-direction:column; align-items:center; }
        .fuel-stat-label { font-size:9px; color:#6b7a90; }
        .fuel-stat-value { font-size:12px; font-weight:700; color:#1a2332; margin-top:2px; }

        /* Cylinder telemetry */
        .tel-panel { background:#fff; border-radius:10px; padding:14px 16px; box-shadow:0 1px 4px rgba(0,0,0,.06); }
        .tel-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
        .tel-title { font-size:12px; font-weight:700; color: #1a73e8; }
        .tel-view { font-size:11px; color:#1a73e8; font-weight:600; cursor:pointer; }
        .tel-cols { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; margin-bottom:10px; text-align:center; }
        .tel-col-label { font-size:9px; color:#6b7a90; font-weight:600; text-transform:uppercase; }
        .tel-items { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; }
        .tel-item { display:flex;background: #f8fafc; flex-direction:column; align-items:center; gap:2px; }
        .tel-item-icon { font-size:14px; }
        .tel-item-val { font-size:12px; font-weight:700; color:#1a2332; }
        .tel-item-unit { font-size:9px; color:#6b7a90; }

        /* Active Alerts */
        .alerts-panel { background:#fff; border-radius:10px; padding:14px 16px; box-shadow:0 1px 4px rgba(0,0,0,.06); }
        .alerts-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
        .alerts-title { font-size:12px; font-weight:700; color: #1a73e8; }
        .alerts-view { font-size:11px; color:#1a73e8; font-weight:600; cursor:pointer; }
        .alert-row { display:flex; align-items:flex-start; gap:10px; padding:9px 0; border-bottom:1px solid #f0f4f8; }
        .alert-row:last-child { border-bottom:none; }
        .alert-icon { width:28px; height:28px; border-radius:6px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; margin-top:2px; }
        .alert-body { flex:1; }
        .alert-title { font-size:11px; font-weight:600; color:#1a2332; }
        .alert-desc { font-size:10px; color:#6b7a90; margin-top:2px; }
        .alert-time { font-size:10px; color:#6b7a90; flex-shrink:0; }

        /* Loading */
        .eq2-loading { display:flex; align-items:center; justify-content:center; height:60vh; font-size:14px; color:#6b7a90; }
      `}</style>

      <div className="eq2-page">
        <EngineHeader
          activePage="Main Engine"
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
        />

        <div className="eq2-inner">
          {/* ── TOP GAUGE ROW ── */}
          <div className="gauge-row" style={{ marginTop: 5 }}>
            <Gauge
              label="ENGINE RPM"
              value={getMetric(shaft, "Engine RPM").value}
              unit="RPM"
              color="#22c55e"
              subtitle="Optimal Range"
              subtitleValue="600 – 750 RPM"
            />
            <Gauge
              label="SHAFT POWER"
              value={getMetric(shaft, "Shaft Power").value}
              unit="kW"
              color="#3b82f6"
              subtitle="Load"
              subtitleValue="72%"
            />
            <Gauge
              label="FUEL INDEX"
              value={getMetric(fuel, "Fuel Index").value}
              unit="%"
              color="#8b5cf6"
              subtitle="Efficiency"
              subtitleValue="Good"
            />
            <Gauge
              label="T/C INLET TEMP"
              value={getMetric(turbo, "T/C Inlet Exhaust Temp").value}
              unit="°C"
              color="#f59e0b"
              subtitle="Limit"
              subtitleValue="< 450 °C"
            />
            <Gauge
              label="SCAVENGE TEMP"
              value={getMetric(scav, "Scavenge Air Receiver Temp").value}
              unit="°C"
              color="#06b6d4"
              subtitle="Optimal Range"
              subtitleValue="30 – 45 °C"
            />
            <Gauge
              label="LO TANK LEVEL"
              value={getMetric(lube, "L.O. Sump Tank Level").value}
              unit="%"
              color="#14b8a6"
              subtitle="Status"
              subtitleValue="Normal"
            />
          </div>


<Row>
          {/* ── MAIN 3-COLUMN GRID ── */}
          <div className="main-grid">
            {/* LEFT: Engine Systems */}
            <div className="sys-panel">
              <div className="panel-title">
                <span className="panel-title-icon">🖥</span> Engine Systems
              </div>
              <SysRow label="Turbo Charger RPM"   metric={getMetric(turbo, "Turbo Charger RPM")} />
              <SysRow label="Fuel Oil Consumption" metric={getMetric(fuel, "FO Consumption / Hour")} />
              <SysRow label="LO Sump Tank Level"   metric={getMetric(lube, "L.O. Sump Tank Level")} />
              <SysRow label="Scavenge Air Pressure" metric={getMetric(scav, "Scavenge Air Inlet Pressure")} />
              <SysRow label="Control Air Pressure" metric={getMetric(boiler, "Control Air Pressure")} />
              <SysRow label="Boiler Fuel Temperature" metric={getMetric(boiler, "Boiler Fuel Temperature")} />
              <a className="view-all" href="#">View All Systems →</a>
            </div>

            {/* CENTER */}
            <div className="center-col">
              {/* Engine Card */}
              {/* Engine Card */}
<div className="engine-card">
  <div className="engine-card-header">
    <div className="engine-card-title">
      Main Engine
      <span className="engine-model-badge">6S60ME-C</span>
    </div>
    <span className="view-details-btn">View Details ↗</span>
  </div>
  <div className="engine-img-wrap">
    <video
      src="/asset/main_engine1.mp4"
      autoPlay loop muted playsInline
      style={{ width: "100%", height: 260, objectFit: "cover", borderRadius: 10, border: "1px solid #e8edf3" }}
    />
  </div>
  <div className="engine-stats-bar">
    <div className="engine-stat">
      <span className="engine-stat-label">Status</span>
      <span className="engine-stat-value running">RUNNING</span>
    </div>
    <div className="engine-stat">
      <span className="engine-stat-label">Engine Load</span>
      <span className="engine-stat-value">72%</span>
    </div>
    <div className="engine-stat">
      <span className="engine-stat-label">Engine Hours</span>
      <span className="engine-stat-value">12,458 h</span>
    </div>
    <div className="engine-stat">
      <span className="engine-stat-label">Total Starts</span>
      <span className="engine-stat-value">156</span>
    </div>
    <div className="engine-stat">
      <span className="engine-stat-label">Last Maintenance</span>
      <span className="engine-stat-value">2 days ago</span>
    </div>
    <div className="engine-stat">
      <span className="engine-stat-label">Next Maintenance</span>
      <span className="engine-stat-value">in 98 hours</span>
    </div>
  </div>
</div>

              {/* Cylinder Section */}
              {/* <div className="cylinder-wrap">
                <CylinderSection date={selectedDate} />
              </div> */}

            </div>

            {/* RIGHT */}
            <div className="right-col">
              {/* Fuel Consumption Chart */}
              <div className="fuel-panel">
                <div className="fuel-panel-header">
                  <span className="fuel-panel-title">Fuel Consumption (L/H)</span>
                  <select className="fuel-time-sel">
                    <option>8 Hours</option>
                    <option>12 Hours</option>
                    <option>24 Hours</option>
                  </select>
                </div>
                <ResponsiveContainer width="100%" height={140}>
                  <AreaChart data={fuelChartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="fuelGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" />
                    <XAxis dataKey="time" tick={{ fill: "#9aa5b4", fontSize: 9 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fill: "#9aa5b4", fontSize: 9 }} tickLine={false} axisLine={false} domain={[0, 2400]} ticks={[0,600,1200,1800,2400]} />
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid #e8edf3", borderRadius: 6, fontSize: 11 }}
                    />
                    <Area type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} fill="url(#fuelGrad)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="fuel-stats">
                  <div className="fuel-stat">
                    <span className="fuel-stat-label">Average</span>
                    <span className="fuel-stat-value">{fcAvg} L/H</span>
                  </div>
                  <div className="fuel-stat">
                    <span className="fuel-stat-label">Min</span>
                    <span className="fuel-stat-value">{fcMin} L/H</span>
                  </div>
                  <div className="fuel-stat">
                    <span className="fuel-stat-label">Max</span>
                    <span className="fuel-stat-value">{fcMax} L/H</span>
                  </div>
                  <div className="fuel-stat">
                    <span className="fuel-stat-label">Total</span>
                    <span className="fuel-stat-value">{fcTotal.toLocaleString()} L</span>
                  </div>
                </div>
              </div>

              {/* ME Cylinder Telemetry */}
              <div className="tel-panel">
                <div className="tel-header">
                  <span className="tel-title">ME Cylinder Telemetry</span>
                  <span className="tel-view">View Details →</span>
                </div>
                <div className="tel-cols">
                  <span className="tel-col-label">Exh. Gas Temp</span>
                  <span className="tel-col-label">Avg Deviation</span>
                  <span className="tel-col-label">Piston Oil Temp</span>
                  <span className="tel-col-label">Jacket Water Temp</span>
                </div>
                <div className="tel-items">
                  <div className="tel-item">
                    <span className="tel-item-icon">🔥</span>
                    <span className="tel-item-val">412 °C</span>
                  </div>
                  <div className="tel-item">
                    <span className="tel-item-icon">📊</span>
                    <span className="tel-item-val">±6.2 °C</span>
                  </div>
                  <div className="tel-item">
                    <span className="tel-item-icon">⏱</span>
                    <span className="tel-item-val">68 °C</span>
                  </div>
                  <div className="tel-item">
                    <span className="tel-item-icon">💧</span>
                    <span className="tel-item-val">32 °C</span>
                  </div>
                </div>
              </div>

            
            </div>
            </div>
            </Row>

            <Row className="mt-2">
               <Col lg={8} >
              {/* Performance Status */}
              <div className="perf-panel">
                <div className="panel-title">Performance Status</div>
                <div className="perf-grid">
                  <PerfCard icon="⚙️" label="Engine RPM"    metric={getMetric(shaft, "Engine RPM")}  subLabel="Optimal" />
                  <PerfCard icon="⚡" label="Shaft Power"   metric={getMetric(shaft, "Shaft Power")} subLabel="72% Load" />
                  <PerfCard icon="🔥" label="T/C Inlet Temp" metric={getMetric(turbo, "T/C Inlet Exhaust Temp")} subLabel="Normal" />
                  <PerfCard icon="💨" label="Scavenge Temp" metric={getMetric(scav, "Scavenge Air Receiver Temp")} subLabel="Optimal" />
                  <PerfCard icon="⛽" label="Fuel Index"    metric={getMetric(fuel, "Fuel Index")} subLabel="Good" />
                  <PerfCard icon="🛢️" label="LO Tank Level"  metric={getMetric(lube, "L.O. Sump Tank Level")} subLabel="Normal" />
                </div>
              </div>
              </Col>
              <Col lg={4} >
                {/* Active Alerts */}
              <div className="alerts-panel">
                <div className="alerts-header">
                  <span className="alerts-title">Active Alerts</span>
                  <span className="alerts-view">View All Alerts →</span>
                </div>
                {alerts.map((a, i) => {
                  const { icon, color, bg } = alertIcon(a.type);
                  return (
                    <div className="alert-row" key={i}>
                      <div className="alert-icon" style={{ background: bg, color }}>
                        {icon}
                      </div>
                      <div className="alert-body">
                        <div className="alert-title" style={{ color }}>{a.title}</div>
                        <div className="alert-desc">{a.desc}</div>
                      </div>
                      <span className="alert-time">{a.time}</span>
                    </div>
                  );
                })}
              </div>
          </Col>
</Row>
        </div>
      </div>
      </div>
    </React.Fragment>
  );
};

export default Equip2;